import { useMemo, useEffect } from "react"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useBackgroundRefresh } from "./useBackgroundRefresh"
import {
  GetSystemMonitoringGraphsDocument,
  GetAssetBatchMonitoringGraphsDocument,
  type GetAssetBatchMonitoringGraphsQuery,
  type GetAssetBatchMonitoringGraphsQueryVariables,
  type GetSystemMonitoringGraphsQuery,
  type GetSystemMonitoringGraphsQueryVariables,
  type GraphRequest,
} from "@hydrologiq/gql-experience/graphql"
import { getSeriesColor } from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import type {
  TimeSeriesDataPoint,
  TimeSeriesSeries,
  AssetConfig,
} from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import {
  type GraphCategory,
  deriveGraphCategory,
  getAggregatedColor,
  getGenerationAssetColor,
  FUEL_COLORS,
  GENERATION_COLORS,
  PEAK_COLOR,
} from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/graphColors"
import type { MonitoringScope } from "../types"

export type { GraphRequest }

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PhaseDataPoint {
  timestamp: string
  /** Consumer asset ID (or assetId_connection for connection-based) */
  consumerId: string
  phase1: number | null
  phase2: number | null
  phase3: number | null
  phase1Peak: number | null
  phase2Peak: number | null
  phase3Peak: number | null
}

type MonitoringRow = GetSystemMonitoringGraphsQuery["getSystemMonitoringGraphs"][number]
type AssetSeries = MonitoringRow["assetBreakdown"][number]
type AggPoint = NonNullable<MonitoringRow["aggregatedData"]>[number]

/** API may return overlay graph rows nested on each primary row. */
type MonitoringRowWithOverlays = MonitoringRow & { overlays?: MonitoringRow[] }

export interface OverlayData {
  graphId: string
  field: string
  unit: string | null
  aggregated?: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<string[]>[]
  }
  breakdown: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<string[]>[]
  }
}

export interface TimescaleMonitoringGraphPayload {
  /** System-level aggregated view (line + optional peak / dual metric) */
  aggregated?: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<string[]>[]
  }
  /** Per-asset breakdown (stacked / multi-line) */
  breakdown: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<string[]>[]
  }
  /** Whether aggregated series exist (toggle target) */
  hasAggregated: boolean
  /** Overlay data from other graphs (e.g., battery SoC on power generation) */
  overlays?: OverlayData[]
  /** Per-consumer per-phase power data for the Phase Balance Chart */
  phaseBalance?: PhaseDataPoint[]
}

// ─── Transformation helpers ─────────────────────────────────────────────────

/**
 * Resolve custom asset config for a breakdown series.
 * Handles multi-connection assets correctly — only an exact assetId+connection
 * match applies for connection-based series.
 */
function resolveAssetConfigForSeries(
  assetConfigs: AssetConfig[] | undefined,
  assetId: string,
  connection?: string | null
): AssetConfig | undefined {
  if (!assetConfigs?.length) return undefined
  const conn = connection?.trim()
  if (conn) {
    const exact = assetConfigs.find((c) => c.assetId === assetId && c.connection === conn)
    if (exact) return exact
    return undefined
  }
  return (
    assetConfigs.find((c) => c.assetId === assetId && !c.connection) ??
    assetConfigs.find((c) => c.assetId === assetId)
  )
}

function breakdownToChart(
  seriesList: AssetSeries[],
  assetConfigs?: AssetConfig[],
  category?: GraphCategory
): { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] } {
  const timestampSet = new Set<string>()
  seriesList.forEach((assetSeries) => {
    assetSeries.data.forEach((point: { time: string }) => {
      timestampSet.add(point.time)
    })
  })
  const sortedTimestamps = Array.from(timestampSet).sort()

  const dataPoints: TimeSeriesDataPoint[] = sortedTimestamps.map((timestamp) => {
    const point: TimeSeriesDataPoint = { timestamp }
    seriesList.forEach((assetSeries) => {
      const dataPoint = assetSeries.data.find((d: { time: string }) => d.time === timestamp)
      const seriesKey = assetSeries.connection
        ? `${assetSeries.assetId}_${assetSeries.connection}`
        : assetSeries.assetId
      const value = dataPoint?.value ?? null
      point[seriesKey] = value

      const peakKey = `${seriesKey}_peak`
      const peakValue =
        dataPoint && "peakValue" in dataPoint
          ? (dataPoint as { peakValue?: number | null }).peakValue
          : undefined
      point[peakKey] = peakValue !== null && peakValue !== undefined ? peakValue : value
    })
    return point
  })

  const series: TimeSeriesSeries<string[]>[] = seriesList.map((assetSeries, index: number) => {
    const seriesKey = assetSeries.connection
      ? `${assetSeries.assetId}_${assetSeries.connection}`
      : assetSeries.assetId
    const config = resolveAssetConfigForSeries(assetConfigs, assetSeries.assetId, assetSeries.connection)
    const fromTimescale = assetSeries.label?.trim()
    const baseName = fromTimescale || assetSeries.assetName || assetSeries.assetId
    const label = baseName
    const generationColor = category === "generation"
      ? getGenerationAssetColor((assetSeries as any).assetClass, baseName)
      : undefined
    return {
      name: seriesKey,
      color: config?.color || generationColor || getSeriesColor(index, seriesList.length, category),
      label: config?.label || label,
      group: config?.group,
      strokeWidth: 2,
      connectNulls: false,
    }
  })

  return { data: dataPoints, series }
}

function aggregatedRowsToChart(
  rows: AggPoint[],
  category?: GraphCategory
): {
  data: TimeSeriesDataPoint[]
  series: TimeSeriesSeries<string[]>[]
} {
  const hasPeak = rows.some((p) => p.peakValue != null && p.peakValue !== undefined)
  const data: TimeSeriesDataPoint[] = rows.map((point) => ({
    timestamp: point.time,
    value: point.value ?? null,
    ...(hasPeak ? { combinedPeak: point.peakValue ?? null } : {}),
  }))
  const aggregatedColor = category ? getAggregatedColor(category) : "#39aed2"
  const series: TimeSeriesSeries<string[]>[] = [
    {
      name: "value",
      color: aggregatedColor,
      label: "Total",
      strokeWidth: 3,
      connectNulls: false,
    },
  ]
  if (hasPeak) {
    series.push({
      name: "combinedPeak",
      color: PEAK_COLOR,
      label: "Combined peak",
      strokeWidth: 2,
      strokeDasharray: "5 5",
      connectNulls: false,
    })
  }
  return { data, series }
}

function mergeSecondaryAggregated(
  primary: { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] },
  secondaryRows: AggPoint[],
  secondaryLabel: string,
  secondaryColor: string = GENERATION_COLORS.secondary
): { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] } {
  const byTime = new Map(secondaryRows.map((p) => [p.time, p.value ?? null]))
  const data = primary.data.map((row) => {
    const ts = row.timestamp as string
    return {
      ...row,
      secondary: byTime.has(ts) ? byTime.get(ts)! : null,
    }
  })
  const series: TimeSeriesSeries<string[]>[] = [
    ...primary.series,
    {
      name: "secondary",
      color: secondaryColor,
      label: secondaryLabel,
      strokeWidth: 2,
      connectNulls: false,
    },
  ]
  return { data, series }
}

function rowToPayload(
  row: MonitoringRow,
  assetConfigs: AssetConfig[] | undefined,
  category?: GraphCategory
): TimescaleMonitoringGraphPayload {
  const breakdown = breakdownToChart(row.assetBreakdown, assetConfigs, category)

  const hasAgg = Boolean(row.aggregatedData && row.aggregatedData.length > 0)
  let aggregated: TimescaleMonitoringGraphPayload["aggregated"]
  if (hasAgg && row.aggregatedData) {
    let chart = aggregatedRowsToChart(row.aggregatedData, category)
    if (row.secondaryAggregated?.length) {
      chart = mergeSecondaryAggregated(
        chart,
        row.secondaryAggregated,
        "Fuel consumed (L)",
        category === "fuel" ? FUEL_COLORS.secondary : undefined
      )
    }
    aggregated = chart
  }

  const overlays: OverlayData[] | undefined = (row as MonitoringRowWithOverlays).overlays?.map((overlay) => {
    let overlayCategory: GraphCategory = "default"
    if (overlay.graphId.includes("__class_battery")) {
      overlayCategory = "battery"
    } else if (overlay.graphId.includes("__role_generation")) {
      overlayCategory = "generation"
    } else if (overlay.graphId.includes("__role_consumer") || overlay.graphId.includes("__aggregationRole_load")) {
      overlayCategory = "load"
    } else if (overlay.graphId.includes("__class_fuel")) {
      overlayCategory = "fuel"
    }

    const overlayBreakdown = breakdownToChart(overlay.assetBreakdown, assetConfigs, overlayCategory)
    const hasOverlayAgg = Boolean(overlay.aggregatedData && overlay.aggregatedData.length > 0)
    let overlayAggregated: OverlayData["aggregated"]
    if (hasOverlayAgg && overlay.aggregatedData) {
      // Overlays are single reference lines — strip peakValue so
      // aggregatedRowsToChart never adds a second "combinedPeak" series,
      // which would produce duplicate legend/tooltip entries.
      const overlayRowsNoPeak = overlay.aggregatedData.map((p) => ({ ...p, peakValue: null }))
      overlayAggregated = aggregatedRowsToChart(overlayRowsNoPeak, overlayCategory)
    }

    const entry: OverlayData = {
      graphId: overlay.graphId,
      field: overlay.field,
      unit: overlay.unit ?? null,
      breakdown: overlayBreakdown,
    }
    if (overlayAggregated) {
      entry.aggregated = overlayAggregated
    }
    return entry
  })

  return {
    breakdown,
    aggregated,
    hasAggregated: hasAgg,
    overlays,
    ...(row.phaseBreakdown?.length
      ? {
          phaseBalance: row.phaseBreakdown.map((point) => ({
            timestamp: point.time,
            consumerId: point.connection
              ? `${point.assetId}_${point.connection}`
              : point.assetId,
            phase1: point.phase1 ?? null,
            phase2: point.phase2 ?? null,
            phase3: point.phase3 ?? null,
            phase1Peak: point.phase1Peak ?? null,
            phase2Peak: point.phase2Peak ?? null,
            phase3Peak: point.phase3Peak ?? null,
          })),
        }
      : {}),
  }
}

// ─── Hook interface ─────────────────────────────────────────────────────────

export interface UseMonitoringGraphsParams {
  scope: MonitoringScope
  start: string
  stop: string
  granularity?: string
  /** Increment to force a refetch for unchanged variables; never sent to GraphQL. */
  refreshKey?: number
  graphs: GraphRequest[]
}

export interface UseMonitoringGraphsResult {
  graphsData: Map<string, TimescaleMonitoringGraphPayload>
  loading: boolean
  error?: Error
  refetch: () => void
}

/**
 * Shared monitoring graphs hook that routes to the correct GraphQL query
 * based on the monitoring scope (system vs parent asset).
 *
 * Both queries return the same MonitoringGraphData[] shape. The hook handles
 * scope-based variable construction and applies the standard row-to-payload
 * transformation for chart rendering.
 */
export const useMonitoringGraphs = (
  params: UseMonitoringGraphsParams,
  assetConfigs?: AssetConfig[]
): UseMonitoringGraphsResult => {
  const { scope, start, stop, granularity = "5m", refreshKey, graphs } = params

  // Route to the correct GraphQL document based on scope type.
  // Both queries return identical MonitoringGraphData[] response shapes.
  const document = scope.type === "system"
    ? GetSystemMonitoringGraphsDocument
    : GetAssetBatchMonitoringGraphsDocument

  const { beginFetch, isBackground } = useBackgroundRefresh()

  const [executeQuery, { data, previousData, loading, error }] = useLazyQueryWithEndpoint<
    GetSystemMonitoringGraphsQuery | GetAssetBatchMonitoringGraphsQuery,
    GetSystemMonitoringGraphsQueryVariables | GetAssetBatchMonitoringGraphsQueryVariables
  >(
    document as any,
    { notifyOnNetworkStatusChange: true },
    "primary"
  )

  // Derive stable primitive values from scope to prevent reference-based re-renders
  const scopeType = scope.type
  const scopeId = scope.type === "system" ? scope.systemId : scope.parentAssetId

  // Stabilize graphs array reference — JSON.stringify provides a stable
  // comparison key so variables only recompute when content actually changes.
  const graphsKey = useMemo(() => JSON.stringify(graphs), [graphs])

  const variables = useMemo(() => {
    if (scopeType === "system") {
      return { systemId: scopeId, start, stop, granularity, graphs }
    }
    return { assetId: scopeId, start, stop, granularity, graphs }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeType, scopeId, start, stop, granularity, graphsKey])

  useEffect(() => {
    if (graphs.length === 0) return
    beginFetch(refreshKey)
    executeQuery({ variables: variables as any })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables, refreshKey])

  // An automatic refresh keeps the previous graphs on screen and reports no loading until the new data arrives.
  const background = isBackground()
  const current = data ?? (background && !error ? previousData : undefined)

  const graphsData = useMemo(() => {
    const map = new Map<string, TimescaleMonitoringGraphPayload>()
    const rows = (current as any)?.getSystemMonitoringGraphs ?? (current as any)?.getAssetBatchMonitoringGraphs
    if (!rows?.length) return map

    // Build category map from graph requests for colour derivation
    const categoryMap = new Map<string, GraphCategory>()
    for (const graphReq of graphs) {
      const category = deriveGraphCategory({
        role: graphReq.role ?? undefined,
        class: graphReq.assetClass ?? undefined,
        field: graphReq.field,
      })
      categoryMap.set(graphReq.graphId, category)
    }

    for (const row of rows) {
      const category = categoryMap.get(row.graphId)
      const payload = rowToPayload(row, assetConfigs, category)
      map.set(row.graphId, payload)
    }
    return map
  }, [current, assetConfigs, graphs])

  const refetch = () => {
    if (graphs.length === 0) return
    executeQuery({ variables: variables as any })
  }

  return {
    graphsData,
    loading: graphs.length === 0 ? false : loading && !background,
    error: error as Error | undefined,
    refetch,
  }
}
